import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Users, Link2, Copy, Check, Trash2, ChevronDown, ChevronRight, Hash, MessageSquare, Mail, RefreshCw, Send } from 'lucide-react';
import type { OrgMembersData, OrgInvitesListData, OrgIntegrationsData, EmailInviteResultStatus } from '@/api-types';

interface OrgWithDetails {
	id: string;
	name: string;
	slug: string;
	role: string;
	memberCount: number;
	expanded: boolean;
	members: OrgMembersData['members'] | null;
	invites: OrgInvitesListData['invites'] | null;
	platformLinks: OrgIntegrationsData['platformLinks'] | null;
}

export function OrgMembersSection() {
	const [orgs, setOrgs] = useState<OrgWithDetails[]>([]);
	const [loading, setLoading] = useState(true);
	const [creatingInvite, setCreatingInvite] = useState<string | null>(null);
	const [copiedToken, setCopiedToken] = useState<string | null>(null);
	const [emailInputs, setEmailInputs] = useState<Record<string, string>>({});
	const [sendingEmails, setSendingEmails] = useState(false);
	const [resendingInvite, setResendingInvite] = useState<string | null>(null);
	const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

	const loadOrgDetails = useCallback((orgId: string, role: string) => {
		Promise.all([
			apiClient.getOrgMembers(orgId).catch(() => ({ success: false as const, data: null })),
			(role === 'admin' || role === 'owner')
				? apiClient.getOrgInvites(orgId).catch(() => ({ success: false as const, data: null }))
				: Promise.resolve(null),
			apiClient.getOrgIntegrations(orgId).catch(() => ({ success: false as const, data: null })),
		]).then(([membersRes, invitesRes, integrationsRes]) => {
			setOrgs(prev => prev.map(o => {
				if (o.id !== orgId) return o;
				return {
					...o,
					members: membersRes?.success ? membersRes.data?.members ?? [] : [],
					invites: invitesRes && invitesRes.success ? invitesRes.data?.invites ?? [] : null,
					platformLinks: integrationsRes?.success ? integrationsRes.data?.platformLinks ?? [] : [],
				};
			}));
		});
	}, []);

	useEffect(() => {
		apiClient.getUserOrgs().then(res => {
			if (res.success && res.data) {
				const orgList = res.data.orgs;
				const isSingle = orgList.length === 1;
				setOrgs(orgList.map(o => ({
					...o,
					expanded: isSingle,
					members: null,
					invites: null,
					platformLinks: null,
				})));
				if (isSingle) {
					loadOrgDetails(orgList[0].id, orgList[0].role);
				}
			}
			setLoading(false);
		}).catch(() => setLoading(false));
	}, [loadOrgDetails]);

	const toggleOrg = useCallback((orgId: string) => {
		setOrgs(prev => prev.map(o => {
			if (o.id !== orgId) return o;
			const expanded = !o.expanded;
			if (expanded && !o.members) {
				loadOrgDetails(orgId, o.role);
			}
			return { ...o, expanded };
		}));
	}, [loadOrgDetails]);

	const handleCreateInvite = async (orgId: string) => {
		setCreatingInvite(orgId);
		try {
			const res = await apiClient.createOrgInvite(orgId, { role: 'member' });
			if (res.success && res.data) {
				const newInvite = res.data.invite;
				setOrgs(prev => prev.map(o => {
					if (o.id !== orgId) return o;
					return {
						...o,
						invites: [...(o.invites || []), {
							...newInvite,
							email: null,
							status: 'pending' as const,
							acceptedBy: null,
							acceptedAt: null,
						}],
					};
				}));
				await copyToClipboard(newInvite.inviteUrl, newInvite.token);
				toast.success('Invite link created and copied');
			} else {
				toast.error('Failed to create invite');
			}
		} catch {
			toast.error('Failed to create invite');
		} finally {
			setCreatingInvite(null);
		}
	};

	const handleSendEmailInvites = async (orgId: string) => {
		const emails = (emailInputs[orgId] ?? '')
			.split(/[,\n]+/)
			.map(e => e.trim())
			.filter(Boolean);

		if (emails.length === 0) {
			toast.error('Enter at least one email address');
			return;
		}
		if (emails.length > 10) {
			toast.error('Maximum 10 emails per request');
			return;
		}

		setSendingEmails(true);
		try {
			const res = await apiClient.sendEmailInvites(orgId, emails);
			if (res.success && res.data) {
				const { results } = res.data;
				const sent = results.filter(r => r.status === 'sent').length;
				const statusMessages: Record<EmailInviteResultStatus, string> = {
					sent: 'Invited',
					already_member: 'Already a member',
					already_invited: 'Already invited',
					invalid_email: 'Invalid email',
					failed: 'Failed to send',
				};

				if (sent > 0) {
					toast.success(`${sent} invite${sent > 1 ? 's' : ''} sent`);
					setEmailInputs(prev => ({ ...prev, [orgId]: '' }));
					loadOrgDetails(orgId, 'admin');
				}

				const issues = results.filter(r => r.status !== 'sent');
				for (const issue of issues) {
					toast.error(`${issue.email}: ${statusMessages[issue.status]}`);
				}
			} else {
				toast.error('Failed to send invites');
			}
		} catch {
			toast.error('Failed to send invites');
		} finally {
			setSendingEmails(false);
		}
	};

	const handleResendInvite = async (orgId: string, inviteId: string) => {
		setResendingInvite(inviteId);
		try {
			const res = await apiClient.resendEmailInvite(orgId, inviteId);
			if (res.success) {
				toast.success('Invite email resent');
			} else {
				toast.error('Failed to resend invite');
			}
		} catch {
			toast.error('Failed to resend invite');
		} finally {
			setResendingInvite(null);
		}
	};

	const handleRevokeInvite = async (orgId: string, inviteId: string) => {
		try {
			const res = await apiClient.revokeOrgInvite(orgId, inviteId);
			if (res.success) {
				setOrgs(prev => prev.map(o => {
					if (o.id !== orgId) return o;
					return { ...o, invites: (o.invites || []).filter(i => i.id !== inviteId) };
				}));
				toast.success('Invite revoked');
			}
		} catch {
			toast.error('Failed to revoke invite');
		}
	};

	const copyToClipboard = async (text: string, token: string) => {
		try {
			await navigator.clipboard.writeText(text);
			if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
			setCopiedToken(token);
			copyTimeoutRef.current = setTimeout(() => setCopiedToken(null), 2000);
		} catch {
			toast.error('Failed to copy');
		}
	};

	if (loading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2 text-lg">
						<Users className="h-5 w-5" /> Organization
					</CardTitle>
				</CardHeader>
				<CardContent>
					<Skeleton className="h-8 w-full" />
				</CardContent>
			</Card>
		);
	}

	if (orgs.length === 0) {
		return null;
	}

	const isSingle = orgs.length === 1;

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-2 text-lg">
					<Users className="h-5 w-5" /> {isSingle ? orgs[0].name : 'Organizations'}
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				{orgs.map(org => (
					<div key={org.id} className={isSingle ? '' : 'border rounded-lg'}>
						{!isSingle && (
							<button
								className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
								onClick={() => toggleOrg(org.id)}
							>
								<div className="flex items-center gap-2">
									{org.expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
									<span className="font-medium">{org.name}</span>
									<Badge variant="secondary" className="text-xs">{org.role}</Badge>
									<span className="text-xs text-muted-foreground">{org.memberCount} member{org.memberCount !== 1 ? 's' : ''}</span>
								</div>
							</button>
						)}

						{org.expanded && (
							<div className={isSingle ? 'space-y-3' : 'border-t px-3 pb-3 space-y-3'}>
								{/* Members list */}
								{org.members ? (
									<div className={isSingle ? '' : 'pt-3'}>
										<h4 className="text-sm font-medium mb-2">Members</h4>
										<div className="space-y-1">
											{org.members.filter(m => !m.userId.startsWith('system-')).map(member => (
												<div key={member.id} className="flex items-center justify-between text-sm py-1">
													<div className="flex items-center gap-2">
														{member.avatarUrl ? (
															<img src={member.avatarUrl} alt="" className="h-5 w-5 rounded-full" />
														) : (
															<div className="h-5 w-5 rounded-full bg-muted" />
														)}
														<span>{member.displayName || member.userId}</span>
													{member.email && (
														<span className="text-muted-foreground">{member.email}</span>
													)}
													</div>
													<Badge variant="outline" className="text-xs">{member.role}</Badge>
												</div>
											))}
											{org.members.filter(m => !m.userId.startsWith('system-')).length === 0 && (
												<p className="text-sm text-muted-foreground">No members yet. Send an invite to add members.</p>
											)}
										</div>
									</div>
								) : (
									<Skeleton className="h-8 w-full mt-3" />
								)}

								{/* Connected Platforms */}
								{org.platformLinks && org.platformLinks.length > 0 && (
									<div className="pt-2">
										<h4 className="text-sm font-medium mb-2">Connected Platforms</h4>
										<div className="space-y-1">
											{org.platformLinks.map(link => (
												<div key={link.id} className="flex items-center justify-between text-sm py-1 px-2 rounded bg-muted/30">
													<div className="flex items-center gap-2">
														{link.platform === 'slack' ? (
															<Hash className="h-4 w-4 text-muted-foreground" />
														) : (
															<MessageSquare className="h-4 w-4 text-muted-foreground" />
														)}
														<span>{link.platformTeamName || link.platform}</span>
														<Badge variant="secondary" className="text-xs capitalize">{link.platform}</Badge>
													</div>
													{link.linkedAt && (
														<span className="text-xs text-muted-foreground">
															Connected {new Date(link.linkedAt).toLocaleDateString()}
														</span>
													)}
												</div>
											))}
										</div>
									</div>
								)}

								{/* Invite management (admin/owner only) */}
								{(org.role === 'admin' || org.role === 'owner') && (
									<div className="pt-2 space-y-4">
										{/* Email invite form */}
										<div>
											<h4 className="text-sm font-medium mb-2">Invite by Email</h4>
											<div className="flex gap-2">
												<input
													type="text"
													value={emailInputs[org.id] ?? ''}
													onChange={e => setEmailInputs(prev => ({ ...prev, [org.id]: e.target.value }))}
													placeholder="Enter email addresses (comma separated)"
													className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
													onKeyDown={e => {
														if (e.key === 'Enter' && !sendingEmails) {
															e.preventDefault();
															handleSendEmailInvites(org.id);
														}
													}}
												/>
												<Button
													variant="default"
													size="sm"
													onClick={() => handleSendEmailInvites(org.id)}
													disabled={sendingEmails || !(emailInputs[org.id] ?? '').trim()}
												>
													<Send className="h-3 w-3 mr-1" />
													{sendingEmails ? 'Sending...' : 'Send'}
												</Button>
											</div>
											<p className="text-xs text-muted-foreground mt-1">
												Up to 10 addresses, comma or newline separated. Invites expire in 7 days.
											</p>
										</div>

										{/* Link invite */}
										<div>
											<div className="flex items-center justify-between mb-2">
												<h4 className="text-sm font-medium">Invite Links</h4>
												<Button
													variant="outline"
													size="sm"
													onClick={() => handleCreateInvite(org.id)}
													disabled={creatingInvite === org.id}
												>
													<Link2 className="h-3 w-3 mr-1" />
													{creatingInvite === org.id ? 'Creating...' : 'New Link'}
												</Button>
											</div>

											{org.invites && org.invites.length > 0 && (
												<div className="space-y-1">
													{org.invites.map(invite => (
														<InviteRow
															key={invite.id}
															invite={invite}
															copiedToken={copiedToken}
															resendingInvite={resendingInvite}
															onCopy={() => copyToClipboard(invite.inviteUrl, invite.token)}
															onRevoke={() => handleRevokeInvite(org.id, invite.id)}
															onResend={() => handleResendInvite(org.id, invite.id)}
														/>
													))}
												</div>
											)}
										</div>
									</div>
								)}
							</div>
						)}
					</div>
				))}
			</CardContent>
		</Card>
	);
}

function InviteRow({
	invite,
	copiedToken,
	resendingInvite,
	onCopy,
	onRevoke,
	onResend,
}: {
	invite: OrgInvitesListData['invites'][number];
	copiedToken: string | null;
	resendingInvite: string | null;
	onCopy: () => void;
	onRevoke: () => void;
	onResend: () => void;
}) {
	const isCopied = copiedToken === invite.token;
	const isEmail = !!invite.email;
	const isResending = resendingInvite === invite.id;

	const statusBadge = () => {
		if (invite.status === 'accepted') {
			return <Badge variant="secondary" className="text-xs">accepted</Badge>;
		}
		if (invite.status === 'expired') {
			return <Badge variant="destructive" className="text-xs">expired</Badge>;
		}
		return <Badge variant="outline" className="text-xs">pending</Badge>;
	};

	return (
		<div className="flex items-center justify-between text-sm py-1.5 px-2 rounded bg-muted/30">
			<div className="flex items-center gap-2 min-w-0 flex-1">
				{isEmail ? (
					<Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
				) : (
					<Link2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
				)}
				{isEmail ? (
					<span className="text-xs truncate">{invite.email}</span>
				) : (
					<code className="text-xs truncate">{invite.inviteUrl}</code>
				)}
				{statusBadge()}
				{!isEmail && invite.useCount > 0 && (
					<span className="text-xs text-muted-foreground whitespace-nowrap">
						{invite.useCount} use{invite.useCount !== 1 ? 's' : ''}
					</span>
				)}
			</div>
			<div className="flex items-center gap-1 ml-2">
				{isEmail && invite.status === 'pending' && (
					<Button
						variant="ghost"
						size="sm"
						className="h-6 w-6 p-0"
						onClick={onResend}
						disabled={isResending}
						aria-label="Resend invite email"
					>
						<RefreshCw className={`h-3 w-3 ${isResending ? 'animate-spin' : ''}`} />
					</Button>
				)}
				{!isEmail && (
					<Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onCopy} aria-label="Copy invite link">
						{isCopied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
					</Button>
				)}
				{invite.status !== 'accepted' && (
					<Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={onRevoke} aria-label="Revoke invite">
						<Trash2 className="h-3 w-3" />
					</Button>
				)}
			</div>
		</div>
	);
}
